import React, { useState, useEffect, useContext } from 'react';
import { Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import DashboardNavbar from '../../../../examples/Navbars/DashboardNavbar';
import DashboardLayout from '../../../../examples/LayoutContainers/DashboardLayout';
import { reportError } from 'shared/services/raygunService';
import { emailValidationRegex } from 'shared/const/regexps';
import ErrorLoggingService from '../../../../shared/services/errorlogging.service';
import useAgencyService from '../useAgencyService';
import AgencyDetailsForm from './agency-details-form';
import DeleteConfirmationDialog from './delete-confirmation-dialog';
import AgencyDetailsActions from './agency-details-actions';
import AgencyNotesModal from './agency-notes-modal';
import { NotificationContext } from 'shared/context/notificationContext';
import { LoaderContext } from 'shared/context/loaderContext';
import Breadcrumbs from '../../../../examples/Breadcrumbs';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';

const AgencyDetails = () => {
  const { showNotification } = useContext(NotificationContext);
  const { setLoading } = useContext(LoaderContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    getAgency,
    deleteAgency,
    updateAgency,
    createAgency,
    createAgencyNote,
    getAgencyNotes,
  } = useAgencyService();
  const errorLoggingService = ErrorLoggingService();
  const [agency, setAgency] = useState(null);
  const [error, setError] = useState(null);
  const isNewAgency = id === 'new' || !id;
  const [isEditMode, setIsEditMode] = useState(isNewAgency);
  const [formData, setFormData] = useState(null);
  const [modifiedFields, setModifiedFields] = useState({});
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notes, setNotes] = useState([]);

  const emptyAgency = {
    identifier: '',
    isAgreementSigned: false,
    email: '',
    brands: [],
    shopName: '',
    mailingAddress: '',
    paymentMethod: '',
    abn: '',
    phoneNumber: '',
    isTrusted: false,
    partner: '',
    country: '',
    bankAccountInfo: '',
    agreementSignedDate: '',
  };

  const getAgencyDetails = async () => {
    try {
      setLoading(true);

      let fetchedAgency = null;

      if (isNewAgency) fetchedAgency = emptyAgency;
      else fetchedAgency = await getAgency(id);

      setAgency(fetchedAgency);

      setFormData(fetchedAgency);
    } catch (err) {
      errorLoggingService.log('Error fetching agency details:', err);

      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => navigate('/agency-manager');

  const handleEditClick = () => setIsEditMode(true);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (!isEditMode || isNewAgency) return;

    const isArray = Array.isArray(value);
    const isModified = isArray
      ? JSON.stringify(agency[field]) !== JSON.stringify(value)
      : agency[field] !== value ||
        (typeof value === 'boolean' && agency[field] !== value);

    setModifiedFields((prev) => {
      const newModifiedFields = { ...prev };
      if (isModified) {
        newModifiedFields[field] = value;
      } else {
        delete newModifiedFields[field];
      }
      return newModifiedFields;
    });
  };

  const notEmptyStringRule = (value) => value && value.trim() !== '';

  const validateForm = () => {
    const validationRules = {
      identifier: notEmptyStringRule,
      email: (value) => value && emailValidationRegex.test(value),
      brands: (value) => value && value.length > 0,
      shopName: notEmptyStringRule,
      paymentMethod: notEmptyStringRule,
      abn: notEmptyStringRule,
      isAgreementSigned: (value) => value !== undefined,
      isTrusted: (value) => value !== undefined,
      partner: notEmptyStringRule,
      country: notEmptyStringRule,
    };

    const errors = Object.entries(validationRules).reduce(
      (acc, [field, validator]) => {
        if (!validator(formData[field])) {
          acc[field] = true;
        }
        return acc;
      },
      {},
    );

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const generateNoteText = (modifiedFields) => {
    const changes = Object.entries(modifiedFields).map(([field, newValue]) => {
      const oldValue = agency[field];
      return {
        field,
        oldValue,
        newValue,
      };
    });

    return JSON.stringify({
      changes,
      changeType: `${changes.map((change) => change.field).join(' & ')} change`,
    });
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showNotification(
        t('AGENCY_MANAGER.AGENCY_DETAILS.formCorrection'),
        'error',
      );
      return;
    }

    setLoading(true);

    try {
      if (isNewAgency) {
        await createAgency({
          ...formData,
        });

        showNotification(
          t('AGENCY_MANAGER.AGENCY_DETAILS.agencyCreated'),
          'success',
        );

        setIsEditMode(false);
        navigate('/agency-manager');
        return;
      }
      const modifiedFieldsCopy = { ...modifiedFields };

      // always update both payment method and isTrusted fields
      if (modifiedFieldsCopy.paymentMethod !== undefined) {
        modifiedFieldsCopy.isTrusted = formData.isTrusted;
      }

      if (modifiedFieldsCopy.isTrusted !== undefined) {
        modifiedFieldsCopy.paymentMethod = formData.paymentMethod;
      }

      await updateAgency(agency.identifier, modifiedFieldsCopy);

      if (Object.keys(modifiedFields).length > 0) {
        const noteText = generateNoteText(modifiedFieldsCopy);
        await createAgencyNote(agency.identifier, noteText);
      }

      showNotification(
        t('AGENCY_MANAGER.AGENCY_DETAILS.agencyUpdated'),
        'success',
      );

      setIsEditMode(false);
      setModifiedFields({});
      navigate('/agency-manager');
    } catch (error) {
      errorLoggingService.log(
        `Error ${isNewAgency ? 'creating' : 'updating'} agency:`,
        error,
      );

      showNotification(
        t(
          `AGENCY_MANAGER.AGENCY_DETAILS.error${
            isNewAgency ? 'Creating' : 'Updating'
          }`,
        ),
        'error',
      );

      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => setIsDeleteDialogOpen(true);

  const handleDialogClose = () => setIsDeleteDialogOpen(false);

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      setIsDeleteDialogOpen(false);
      await deleteAgency(agency.identifier);

      showNotification(
        t('AGENCY_MANAGER.AGENCY_DETAILS.agencyDeleted'),
        'success',
      );

      navigate('/agency-manager');
    } catch (error) {
      errorLoggingService.log(
        `Error deleting agency with ID: ${agency.identifier}`,
        error,
      );

      showNotification(
        t('AGENCY_MANAGER.AGENCY_DETAILS.errorDeleting'),
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isNewAgency) {
      navigate('/agency-manager');
    } else {
      setIsEditMode(false);
      setFormData(agency);
      setModifiedFields({});
    }
  };

  const handleNotesClick = async () => {
    try {
      setLoading(true);
      const fetchedNotes = await getAgencyNotes(agency.identifier);
      setNotes(fetchedNotes);
      setShowNotesModal(true);
    } catch (err) {
      errorLoggingService.log('Error fetching agency notes:', err);
      showNotification(t('AGENCY_MANAGER.AGENCY_NOTES.errorLoading'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAgencyDetails();
  }, []);

  if (error)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box data-testid="AgencyDetails">
          <p>
            {t('AGENCY_MANAGER.AGENCY_DETAILS.errorLoading')} {error?.message}
          </p>
        </Box>
      </DashboardLayout>
    );

  if (!agency || !formData)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box data-testid="AgencyDetails">
          <p>{t('AGENCY_MANAGER.AGENCY_DETAILS.loadingAgencyDetails')}</p>
        </Box>
      </DashboardLayout>
    );

  return (
    <DashboardLayout>
      <DashboardNavbar>
        <Breadcrumbs
          title={id}
          route={[
            {
              title: t('AGENCY_MANAGER.AGENCY_DETAILS.title'),
              link: '/agency-manager',
            },
            {
              title: id,
            },
          ]}
        />
      </DashboardNavbar>
      <Box data-testid="AgencyDetails">
        <SoftBox
          sx={{
            marginTop: '6rem',
          }}
        >
          <SoftTypography
            variant="h2"
            fontWeight="bold"
            sx={{ marginBottom: '2rem' }}
          >
            {t('AGENCY_MANAGER.AGENCY_DETAILS.title')}
          </SoftTypography>
        </SoftBox>

        <AgencyDetailsForm
          formData={formData}
          formErrors={formErrors}
          isEditMode={isEditMode}
          isNewAgency={isNewAgency}
          setFormData={handleFormChange}
        />
      </Box>
      <AgencyDetailsActions
        isEditMode={isEditMode}
        handleEditClick={handleEditClick}
        handleSave={handleSave}
        handleCancel={handleCancel}
        handleClose={handleClose}
        handleNotesClick={handleNotesClick}
      />
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDeleteConfirm}
        title={t('AGENCY_MANAGER.AGENCY_DETAILS.confirmDeletion')}
        description={t('AGENCY_MANAGER.AGENCY_DETAILS.confirmDeleteMessage')}
        confirmButtonText={t('COMMON.delete')}
        cancelButtonText={t('COMMON.cancel')}
      />
      <AgencyNotesModal
        open={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        notes={notes}
      />
    </DashboardLayout>
  );
};

export default AgencyDetails;
